import React, { useEffect } from 'react';
import { Controller } from 'react-hook-form';

import { FormError, HelpText } from '@Forms/fields.jsx';

const clamp = (value, min, max) => {
  if (typeof min === 'number') value = Math.max(min, value);
  if (typeof max === 'number') value = Math.min(max, value);
  return value;
};

const StepperField = ({
  control,
  defaultValue = 0,
  error,
  helpText,
  label,
  name,
  rules,
  min = 0,
  max = 100,
  step = 1,
  disabled = false,
  placeholder = '',
  inputMode = 'numeric',
  ...props
}) => {
  return (
    <div
      className={
        `sb-form-item-formgroup sb-form-item-input ` +
        (error ? 'sb-form-item-error' : '')
      }
    >
      {label && <label htmlFor={name}>{label}</label>}
      <div className="sb-form-item-content">
        <Controller
          control={control}
          name={name}
          rules={rules}
          defaultValue={defaultValue}
          render={({ field, fieldState }) => {
            const raw = field.value;

            // If the backend/defaultValues provide null/undefined, initialize to defaultValue (0)
            useEffect(() => {
              if (raw === null || raw === undefined) {
                field.onChange(defaultValue);
              }
            }, [defaultValue, field, fieldState?.isTouched, raw]);

            const parsed =
              typeof raw === 'number'
                ? Math.trunc(raw)
                : Number.parseInt(raw, 10);
            const current =
              Number.isFinite(parsed) && !Number.isNaN(parsed) ? parsed : '';

            const currentForButtons =
              current === '' ? (typeof min === 'number' ? min : 0) : current;

            const decDisabled =
              disabled || (typeof min === 'number' && currentForButtons <= min);
            const incDisabled =
              disabled || (typeof max === 'number' && currentForButtons >= max);

            const setNext = (next) => {
              const clamped = clamp(next, min, max);
              // Store as integer/number in react-hook-form state
              field.onChange(Math.trunc(clamped));
            };

            return (
              <div className="sb-flex" style={{ gap: 8, alignItems: 'center' }}>
                <button
                  type="button"
                  className="sb-btn sb-btn-cancel"
                  disabled={decDisabled}
                  onClick={() => setNext(currentForButtons - step)}
                >
                  -
                </button>
                <button
                  type="button"
                  className="sb-btn sb-btn-save"
                  disabled={incDisabled}
                  onClick={() => setNext(currentForButtons + step)}
                >
                  +
                </button>
                <input
                  id={name}
                  type="number"
                  inputMode={inputMode}
                  min={min}
                  max={max}
                  step={step}
                  placeholder={placeholder}
                  aria-invalid={error ? 'true' : 'false'}
                  disabled={disabled}
                  value={raw ?? ''}
                  onChange={(e) => {
                    const nextRaw = e.target.value;
                    if (nextRaw === '') {
                      // keep UI empty, store "no value" in form state
                      field.onChange(undefined);
                      return;
                    }
                    const nextParsed = Number.parseInt(nextRaw, 10);
                    if (
                      !Number.isFinite(nextParsed) ||
                      Number.isNaN(nextParsed)
                    ) {
                      field.onChange(undefined);
                      return;
                    }
                    setNext(nextParsed);
                  }}
                  onBlur={field.onBlur}
                  ref={field.ref}
                  {...props}
                />
              </div>
            );
          }}
        />

        {helpText && <HelpText message={helpText} />}
        {error && <FormError message={error.message} />}
      </div>
    </div>
  );
};

export default StepperField;
